import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Quote, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { DynamicIcon } from '@/utils/iconMap';
import { resolveImageUrl } from '@/utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1558877310-038dd6e020a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxob25nJTIwa29uZyUyMGNpdHklMjBzdHJlZXQlMjB0cmFmZmljfGVufDB8fHx8MTc3MTgyNDA0Mnww&ixlib=rb-4.1.0&q=85';

const HomePage = () => {
  const { settings } = useSiteSettings();
  const [instructors, setInstructors] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [homeFeatures, setHomeFeatures] = useState([]);
  const [gallery, setGallery] = useState([]);
  const marqueeRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    const display = s.gallery_display || 'marquee';
    if (!gallery.length || display !== 'marquee') return;
    const track = marqueeRef.current;
    if (!track) return;

    let pos = 0;
    let lastTs = null;
    let halfWidth = 0;
    const speed = Number(s.gallery_speed) || 30;

    // Use ResizeObserver to update halfWidth as images load and the track grows
    const ro = new ResizeObserver(() => {
      const sw = track.scrollWidth;
      if (sw > 0) halfWidth = sw / 2;
    });
    ro.observe(track);

    const animate = (ts) => {
      if (!pausedRef.current) {
        if (halfWidth > 0) {
          if (lastTs !== null) {
            const pxPerS = halfWidth / speed;
            pos = (pos + pxPerS * (ts - lastTs) / 1000) % halfWidth;
            track.style.transform = `translate3d(${-pos}px, 0, 0)`;
          }
          lastTs = ts;
        }
      } else {
        lastTs = null;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [gallery, settings]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [instrRes, testRes, featRes] = await Promise.all([
          axios.get(`${API_URL}/instructors`),
          axios.get(`${API_URL}/testimonials?featured_only=true`),
          axios.get(`${API_URL}/home-features`),
        ]);
        setInstructors(instrRes.data.slice(0, 6));
        setTestimonials(testRes.data.slice(0, 3));
        setHomeFeatures(featRes.data || []);
      } catch (e) {
        console.error('Failed to fetch data:', e);
      }
      // Gallery is non-critical — fetch separately so it never breaks main content
      try {
        const galleryRes = await axios.get(`${API_URL}/gallery`);
        setGallery(galleryRes.data || []);
      } catch (e) {
        // silently ignore if gallery endpoint not yet available
      }
    };
    fetchData();
  }, []);

  const s = settings || {};
  const whatsapp = s.whatsapp || '85295858258';
  const heroTitle = s.hero_title || '學車選擇智駕駛\n一Take Pass不是夢';
  const heroSubtitle = s.hero_subtitle || '經驗豐富的專業導師團隊，悉心教導每一位學員，高合格率證明實力。';
  const heroCtaText = s.hero_cta_text || 'WhatsApp 即時查詢';
  const heroImageUrl = resolveImageUrl(s.hero_image_url) || DEFAULT_HERO_IMAGE;
  const featuresTitle = s.features_title || '為何選擇智駕駛？';
  const featuresSubtitle = s.features_subtitle || '我們的優勢讓您學車更輕鬆';
  const instructorsTitle = s.instructors_title || '專業導師團隊';
  const instructorsSubtitle = s.instructors_subtitle || '經驗豐富、悉心教導';
  const testimonialsTitle = s.testimonials_title || '學員成功分享';
  const testimonialsSubtitle = s.testimonials_subtitle || '聽聽他們的合格故事';
  const ctaTitle = s.cta_title || '準備好開始學車了嗎？';
  const ctaSubtitle = s.cta_subtitle || '立即聯絡我們，免費諮詢課程內容';

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 md:py-32">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={heroImageUrl}
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" data-testid="hero-title">
                {heroTitle.split('\n').map((line, i) => {
                  const parts = line.split('智駕駛');
                  return (
                    <span key={i} className="flex flex-wrap items-end gap-x-1">
                      {parts.map((part, j) => (
                        <span key={j} className="inline-flex items-end">
                          {part}
                          {j < parts.length - 1 && (
                            <span className="inline-flex flex-col items-start ml-6">
                              <img
                                src="https://res.cloudinary.com/db3glh6zb/image/upload/v1777476713/wise-driving/ab51b3fc-bab1-49af-8b83-d2aabd4cadcc.png"
                                alt="智駕駛"
                                className="block"
                                style={{ height: '1.2em', filter: 'brightness(0) invert(1)' }}
                              />
                              <img
                                src="https://res.cloudinary.com/db3glh6zb/image/upload/v1777476383/wise-driving/6a897a01-1af3-41bb-939e-234664a01c87.png"
                                alt="Wise Driving"
                                className="block mt-1"
                                style={{ height: '0.5em', filter: 'brightness(0) invert(1)' }}
                              />
                            </span>
                          )}
                        </span>
                      ))}
                      {i < heroTitle.split('\n').length - 1 && <br />}
                    </span>
                  );
                })}
              </h1>
              <p className="text-lg md:text-xl mb-8 text-blue-100 leading-relaxed">
                {heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-green-500 hover:bg-green-600 text-white h-12 px-8 rounded-full font-semibold text-lg shadow-lg"
                >
                  <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    {heroCtaText}
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-2 border-white hover:bg-white hover:text-blue-800 h-12 px-8 rounded-full font-semibold text-lg backdrop-blur-sm"
                >
                  <Link to="/courses">課程資訊</Link>
                </Button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{featuresTitle}</h2>
            <p className="text-lg text-muted-foreground">{featuresSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {homeFeatures.map((feature) => (
              <Card key={feature.id} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/50">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 group-hover:scale-110 transition-transform">
                    <DynamicIcon name={feature.icon} className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instructors */}
      {instructors.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">{instructorsTitle}</h2>
              <p className="text-lg text-muted-foreground">{instructorsSubtitle}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {instructors.map((instructor) => (
                <div key={instructor.id} className="text-center group">
                  <div className="mb-3">
                    <img
                      src={resolveImageUrl(instructor.image_url) || '/images/tutors/placeholder.jpg'}
                      alt={instructor.name}
                      className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover mx-auto border-4 border-blue-100 shadow-md group-hover:border-blue-300 transition-colors"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900">{instructor.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">{instructor.title}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg" className="rounded-full">
                <Link to="/instructors">
                  查看所有導師 <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">{testimonialsTitle}</h2>
              <p className="text-lg text-muted-foreground">{testimonialsSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <Card key={t.id} className="border-0 shadow-md hover:shadow-lg transition-all bg-white">
                  <CardContent className="p-6">
                    <Quote className="h-6 w-6 text-blue-200 mb-3" />
                    <p className="text-sm leading-relaxed mb-5 text-gray-700 line-clamp-5">{t.content}</p>
                    <div className="flex items-center gap-3 pt-4 border-t">
                      {t.image_url && (
                        <img src={resolveImageUrl(t.image_url)} alt={t.student_name} className="w-10 h-10 rounded-full object-cover border-2 border-blue-100" />
                      )}
                      <div>
                        <div className="font-semibold text-sm">{t.student_name}</div>
                        <div className="text-xs text-muted-foreground">導師：{t.instructor_name}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg" className="rounded-full">
                <Link to="/testimonials">
                  查看更多心得 <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery */}
      {gallery.length > 0 && (() => {
        const display = s.gallery_display || 'marquee';
        const speed = s.gallery_speed || 30;
        const imgH = s.gallery_height || 192;
        if (display === 'marquee') {
          const doubled = [...gallery, ...gallery];
          return (
            <section className="py-10 bg-white overflow-hidden">
              <div
                ref={marqueeRef}
                style={{
                  display: 'flex',
                  width: 'max-content',
                  willChange: 'transform',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                }}
                onMouseEnter={() => { pausedRef.current = true; }}
                onMouseLeave={() => { pausedRef.current = false; }}
                onTouchEnd={() => { pausedRef.current = !pausedRef.current; }}
              >
                {doubled.map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="flex-shrink-0 mx-2">
                    <img
                      src={resolveImageUrl(item.image_url)}
                      alt={item.caption || `相片 ${idx + 1}`}
                      className="w-auto rounded-xl object-cover shadow-md"
                      style={{ height: `${imgH}px` }}
                      loading="eager"
                    />
                    {item.caption && (
                      <p className="text-xs text-center text-muted-foreground mt-1">{item.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        }
        return (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((item) => (
                  <div key={item.id} className="group relative overflow-hidden rounded-xl shadow-md">
                    <img
                      src={resolveImageUrl(item.image_url)}
                      alt={item.caption || ''}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      style={{ height: `${imgH}px` }}
                    />
                    {item.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2 text-center">
                        {item.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">{ctaTitle}</h2>
          <p className="text-lg md:text-xl mb-8 text-blue-100">{ctaSubtitle}</p>
          <Button
            asChild
            size="lg"
            className="bg-green-500 hover:bg-green-600 text-white h-14 px-12 rounded-full font-semibold text-lg shadow-lg"
          >
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-6 w-6" />
              {heroCtaText}
            </a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
